/**
 * Pure derivations for the refill-requirements feature. No React, no I/O — unit-tested
 * in isolation and consumed by `RefillRequirementsCubit` selectors and the UI.
 */
import {
  RefillCohort,
  RefillRequirementState,
  RefillRequirementType,
  type RefillCardVariant,
  type RefillCountdown,
  type RefillRequirement,
  type RefillRequirementsProgress,
  type RefillRequirementsResponse
} from "./RefillRequirementsTypes";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Length of the countdown-ring window in days. The ring fills over the final two weeks
 * before the target: full at 14 days out, half at 7, empty at 0 (Figma node 5978:11701).
 */
const COUNTDOWN_WINDOW_DAYS = 14;

/**
 * Whole-UTC-day index for a timestamp (days since the epoch). Comparing indices measures
 * calendar days directly, so the target's time-of-day component never skews the count —
 * e.g. a target at `02:01Z` and `now` three calendar days later is 3 days apart, not 4.
 */
const utcDayIndex = (ms: number): number => Math.floor(ms / MS_PER_DAY);

/**
 * Days-until-refill at or below which an outstanding refill becomes "action needed"
 * (orange) rather than an upcoming reminder (purple). Also drives the hub countdown tone.
 */
export const REFILL_URGENT_THRESHOLD_DAYS = 7;

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

/** Normalize one raw requirement — coerce nullable `expiresAt` to null; pass counts through. */
export const parseRefillRequirement = (
  raw: RefillRequirement
): RefillRequirement => ({
  type: raw.type,
  label: raw.label,
  fulfilled: raw.fulfilled,
  count: raw.count,
  minCount: raw.minCount,
  expiresAt: raw.expiresAt ?? null,
  state: raw.state
});

/**
 * Normalize a raw response into the domain shape: parse each requirement, default the
 * progress counts from the list when the server omits them, and coerce nullable dates.
 * This is the single place counts are derived — downstream selectors trust the result.
 */
export const parseRefillRequirementsResponse = (
  raw: RefillRequirementsResponse
): RefillRequirementsResponse => {
  const requirements = (raw.requirements ?? []).map(parseRefillRequirement);
  return {
    goalType: raw.goalType,
    medicationName: raw.medicationName ?? null,
    status: raw.status,
    allFulfilled: raw.allFulfilled,
    completedCount:
      typeof raw.completedCount === "number"
        ? raw.completedCount
        : requirements.filter((r) => r.fulfilled).length,
    totalCount:
      typeof raw.totalCount === "number" ? raw.totalCount : requirements.length,
    dueAt: raw.dueAt ?? null,
    windowOpensAt: raw.windowOpensAt ?? null,
    windowClosesAt: raw.windowClosesAt ?? null,
    nextExpiresAt: raw.nextExpiresAt ?? null,
    requirements
  };
};

/**
 * Whole days from `now` until the refill is due. Never negative; 0 for missing/invalid
 * input (the DTO may send `dueAt: null` before the window opens).
 */
export const getDaysUntilNextRefill = (
  dueAt: string | Date | null | undefined,
  now: Date = new Date()
): number => {
  if (!dueAt) {
    return 0;
  }
  const target = dueAt instanceof Date ? dueAt : new Date(dueAt);
  const ms = target.getTime();
  if (Number.isNaN(ms)) {
    return 0;
  }
  return Math.max(0, Math.ceil((ms - now.getTime()) / MS_PER_DAY));
};

/**
 * Whole calendar days since the target/anchor *date* (`dueAt`) — the "overdue by N days"
 * count for the hub countdown. The target date is **inclusive** (the deadline day is 0, the
 * next day is 1, …) and compared on UTC day boundaries, so the target's time-of-day never
 * inflates the count. Never negative; 0 when not yet due / missing / invalid.
 */
export const getDaysOverdue = (
  dueAt: string | Date | null | undefined,
  now: Date = new Date()
): number => {
  if (!dueAt) {
    return 0;
  }
  const target = dueAt instanceof Date ? dueAt : new Date(dueAt);
  const ms = target.getTime();
  if (Number.isNaN(ms)) {
    return 0;
  }
  return Math.max(0, utcDayIndex(now.getTime()) - utcDayIndex(ms));
};

/**
 * Whole calendar days until the renewal window opens — the pre-window countdown (FEWEB-2666).
 * Compared on UTC day boundaries so it agrees with `isRenewalWindowOpen`, which decides *which*
 * countdown is showing: a fractional-day difference here and a whole-day gate there would let
 * the badge read "1 day until refill opens" on a day the window is already open. Never
 * negative; 0 for missing/invalid input or once the open day has arrived.
 */
export const getDaysUntilRenewalWindowOpens = (
  windowOpensAt: string | Date | null | undefined,
  now: Date = new Date()
): number => {
  if (!windowOpensAt) {
    return 0;
  }
  const opensAt =
    windowOpensAt instanceof Date ? windowOpensAt : new Date(windowOpensAt);
  const ms = opensAt.getTime();
  if (Number.isNaN(ms)) {
    return 0;
  }
  return Math.max(0, utcDayIndex(ms) - utcDayIndex(now.getTime()));
};

/**
 * Fraction of the countdown ring to fill (1 = full, 0 = empty at the target), clamped to
 * [0,1]. The ring spans the final `windowDays` before the target: full at/above the window
 * length, half at the midpoint, empty at 0. Defaults to a 14-day window per Figma, so 14 →
 * full, 13 → almost full, 7 → half, 0 → empty.
 */
export const getCountdownFraction = (
  daysRemaining: number,
  windowDays: number = COUNTDOWN_WINDOW_DAYS
): number => {
  if (windowDays <= 0) {
    return 0;
  }
  return clamp01(daysRemaining / windowDays);
};

/**
 * Fraction of the overdue ring to fill (red), on the same fixed 14-day scale as the
 * countdown: half at 7 days overdue, full at 14. Clamped to [0,1]. Keeping one scale for both
 * directions means the ring reads consistently — a full circle is always 14 days either way.
 */
export const getOverdueFraction = (
  data: RefillRequirementsResponse,
  now: Date = new Date()
): number => getCountdownFraction(getDaysOverdue(data.dueAt, now));

/**
 * Progress for the header bar. Prefers the server `completedCount`/`totalCount`;
 * falls back to deriving from the requirements list.
 */
export const getRequirementsProgress = (
  data: RefillRequirementsResponse | undefined
): RefillRequirementsProgress => {
  if (!data) {
    return { completed: 0, total: 0, percent: 0 };
  }
  // Counts are already derived by parseRefillRequirementsResponse — trust the struct.
  const { completedCount: completed, totalCount: total } = data;
  return { completed, total, percent: total === 0 ? 0 : completed / total };
};

/**
 * Renewal status at which the refill check-in survey unlocks. The backend flips to this once
 * the other requirements are met and the survey is the outstanding step; before it (e.g.
 * ACTIVE_INTAKE, BLOCKED) the check-in row stays display-only so members can't submit early.
 */
const PENDING_SURVEY_STATUS = "PENDING_SURVEY";

/**
 * The refill check-in is the one requirement a member can act on from the hub: tapping its
 * row opens the refill survey. Actionable only while unfulfilled *and* the renewal has reached
 * `PENDING_SURVEY` — the backend's signal that the remaining conditions are met. A submitted
 * check-in isn't re-opened, and before `PENDING_SURVEY` the row stays display-only.
 */
export const isRefillCheckInActionable = (
  requirement: RefillRequirement,
  status: string | undefined
): boolean =>
  requirement.type === RefillRequirementType.REFILL_CHECK_IN_SUBMITTED &&
  !requirement.fulfilled &&
  status === PENDING_SURVEY_STATUS;

/**
 * Whether the backend has closed this requirement to input (BACK-6069). The single place the
 * `LOCKED` state is read, so a rename on the server is one edit here. Requirements from before
 * the field existed carry no state and are never locked.
 */
export const isRequirementLocked = (requirement: RefillRequirement): boolean =>
  requirement.state === RefillRequirementState.LOCKED;

/** Whether every applicable requirement is met. Server-authoritative — `allFulfilled` is
 * required in the contract and set by the parser, so we trust it directly. */
export const areAllRequirementsMet = (
  data: RefillRequirementsResponse
): boolean => data.allFulfilled;

/** Renewal statuses that remove the card — the renewal is already underway or done. */
const RENEWAL_TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  "IN_PROGRESS",
  "READY",
  "PICKED_UP",
  "DISCONTINUED"
]);

const isRenewalCompleted = (data: RefillRequirementsResponse): boolean =>
  typeof data.status === "string" && RENEWAL_TERMINAL_STATUSES.has(data.status);

/**
 * Whether `now` falls within the renewal window, compared on whole UTC days so both the open
 * and close days are inclusive (consistent with the overdue count) — the card stays visible
 * through the entire `windowClosesAt` day. Requires a known open edge (`windowOpensAt`); an
 * absent `windowClosesAt` leaves the window open-ended.
 */
export const isRenewalWindowOpen = (
  data: RefillRequirementsResponse,
  now: Date = new Date()
): boolean => {
  if (!data.windowOpensAt) {
    return false;
  }
  const today = utcDayIndex(now.getTime());
  const opensAt = new Date(data.windowOpensAt).getTime();
  if (Number.isNaN(opensAt) || today < utcDayIndex(opensAt)) {
    return false;
  }
  if (data.windowClosesAt) {
    const closesAt = new Date(data.windowClosesAt).getTime();
    if (!Number.isNaN(closesAt) && today > utcDayIndex(closesAt)) {
      return false;
    }
  }
  return true;
};

/**
 * Whether the renewal window has not opened yet — the hub's pre-window treatment (FEWEB-2666).
 * Deliberately not `!isRenewalWindowOpen`, which is also true *after* the window closes: a
 * closed-out renewal must keep the overdue countdown, not flip back to "days until refill
 * opens". Requires a known open edge, so non-concierge renewals (no window) are never
 * pre-window. Same UTC day basis as the window gate, so the open day itself is already open.
 */
export const isBeforeRenewalWindow = (
  data: RefillRequirementsResponse,
  now: Date = new Date()
): boolean => {
  if (!data.windowOpensAt) {
    return false;
  }
  const opensAt = new Date(data.windowOpensAt).getTime();
  return (
    !Number.isNaN(opensAt) && utcDayIndex(now.getTime()) < utcDayIndex(opensAt)
  );
};

/**
 * Whether the target/anchor date (`dueAt`) has passed — the card goes overdue. The target is
 * the renewal deadline day and is **inclusive**: the member is not overdue *on* it, only once
 * it has fully elapsed. Compared on UTC day boundaries, so overdue begins at the start of the
 * next calendar day and the target's time-of-day never makes the deadline day overdue.
 */
export const isRenewalOverdue = (
  data: RefillRequirementsResponse,
  now: Date = new Date()
): boolean => {
  if (!data.dueAt) {
    return false;
  }
  const target = new Date(data.dueAt).getTime();
  return (
    !Number.isNaN(target) && utcDayIndex(now.getTime()) > utcDayIndex(target)
  );
};

/**
 * The hub hero countdown, resolved in one pass so the dialog reads a value instead of
 * re-deriving four interdependent ones inline. Three mutually exclusive readings, in priority
 * order:
 *
 * - **Pre-window** — count to `windowOpensAt`, always green. The urgency threshold is
 *   suppressed because nothing is actionable yet: orange would be telling the member to hurry
 *   on a checklist the backend has closed.
 * - **Overdue** — count past `dueAt`, red, filling on the same 14-day scale.
 * - **In-window** — count down to `dueAt`, orange once inside the urgent threshold.
 *
 * No renewal yields an empty green countdown; the hub hides the badge for non-concierge
 * members anyway, and a zeroed value is easier to render than an absent one.
 */
export const getHubCountdown = (
  data: RefillRequirementsResponse | undefined,
  now: Date = new Date()
): RefillCountdown => {
  if (!data) {
    return {
      days: 0,
      fraction: 0,
      tone: "green",
      overdue: false,
      beforeWindow: false
    };
  }

  if (isBeforeRenewalWindow(data, now)) {
    const days = getDaysUntilRenewalWindowOpens(data.windowOpensAt, now);
    return {
      days,
      fraction: getCountdownFraction(days),
      tone: "green",
      overdue: false,
      beforeWindow: true
    };
  }

  if (isRenewalOverdue(data, now)) {
    return {
      days: getDaysOverdue(data.dueAt, now),
      fraction: getOverdueFraction(data, now),
      tone: "red",
      overdue: true,
      beforeWindow: false
    };
  }

  const days = getDaysUntilNextRefill(data.dueAt, now);
  return {
    days,
    fraction: getCountdownFraction(days),
    tone: days <= REFILL_URGENT_THRESHOLD_DAYS ? "orange" : "green",
    overdue: false,
    beforeWindow: false
  };
};

/**
 * Home-screen refill card state by cohort (FEWEB-2502).
 * - `THROUGH_9AM` (concierge): shown while the renewal window is open — `windowOpen` (purple)
 *   before the target date, `overdue` (orange) once it passes. Hidden when the renewal is
 *   underway/done (terminal status) or the window has ended.
 * - `OUTSIDE_9AM` (non-concierge): always visible when there's a renewal — `secured` (green)
 *   when all requirements are met, else `actionRequired` (red).
 * - No renewal (no data) → `hidden`.
 */
export const getRefillCardVariant = (
  data: RefillRequirementsResponse | undefined,
  cohort: RefillCohort,
  now: Date = new Date()
): RefillCardVariant => {
  if (!data) {
    return "hidden";
  }

  if (cohort === RefillCohort.THROUGH_9AM) {
    if (isRenewalCompleted(data) || !isRenewalWindowOpen(data, now)) {
      return "hidden";
    }
    return isRenewalOverdue(data, now) ? "overdue" : "windowOpen";
  }

  return areAllRequirementsMet(data) ? "secured" : "actionRequired";
};
